import moment from 'moment';
import React, { useCallback, useState } from 'react';
import VoxelDialog from '../../../../components/voxel/Dialogs/VoxelDialog';
import VoxelDialogActionCancel from '../../../../components/voxel/Dialogs/VoxelDialogActionCancel';
import VoxelDialogActionSubmit from '../../../../components/voxel/Dialogs/VoxelDialogActionSubmit';
import VoxelDialogActions from '../../../../components/voxel/Dialogs/VoxelDialogActions';
import VoxelDialogContent from '../../../../components/voxel/Dialogs/VoxelDialogContent';
import VoxelText from '../../../../components/voxel/Text/VoxelText';
import VoxelDivider from '../../../../components/voxel/VoxelDivider';
import { SearchRequestTypes } from '../../../../components/muiLegacy/Tables/KeywordTable/ToolbarButtons/AddKeywords/AddBasicKeywords/logic/SearchModels';
import { projectsApiService } from '../../../../graphql/projects/projectsApiService';
import { CircularProgress } from '@material-ui/core';
import { UpdateGoogleAdsRequestCommandGq } from '../../../../graphql/projects/mutations/googleAds/useUpdateGoogleAdsRequestGq';
import { zIndexVoxel } from '../../../../logic/zIndexVoxel';

export interface DeleteRequestDialogProps {
  projectId: number;
  requestId?: number;
  setRequestId: React.Dispatch<React.SetStateAction<number>>;
  openPopover: () => void;
}

export const DeleteRequestDialog: React.FC<DeleteRequestDialogProps> = ({
  requestId,
  projectId,
  setRequestId,
  openPopover,
}) => {
  // Data retrieval
  const dataquery = projectsApiService.queries.googleAds.useGetGoogleAdsRequest(
    { id: requestId, projectId },
    { skip: !requestId }
  );

  const requestAnalysis = dataquery?.result;

  // Closing
  const handleClose = () => {
    setRequestId(undefined);
    openPopover();
  };

  // Delete functionality
  const controlMutation = projectsApiService.mutations.googleAds.useUpdateGoogleAdsRequest();
  const deleteMutation = projectsApiService.mutations.googleAds.useDeleteGoogleAdsRequest();
  const [deleting, setDeleting] = useState<boolean>(false);
  const handleDelete = useCallback(
    (event: React.MouseEvent<any>) => {
      event.stopPropagation();
      setDeleting(true);
      controlMutation({
        requestId: requestAnalysis.request.id,
        projectId: requestAnalysis.request.parentProjectId,
        paused: true,
      }).then(() =>
        deleteMutation({
          requestId: requestAnalysis.request.id,
          projectId: requestAnalysis.request.parentProjectId,
        }).finally(() => {
          handleClose();
          setDeleting(false);
        })
      );
    },
    [requestAnalysis]
  );
  if (!dataquery?.result && !dataquery.query.loading) return null;

  if (dataquery.query.loading)
    return (
      <VoxelDialog
        open={requestId !== undefined}
        onClose={handleClose}
        title="Delete keyword search"
      >
        <VoxelDialogContent>
          <div className="p-4 flex flex-row items-center justify-center">
            <CircularProgress />
          </div>
        </VoxelDialogContent>
      </VoxelDialog>
    );

  return (
    <VoxelDialog
      open={requestId !== undefined}
      onClose={() => (deleting ? undefined : handleClose())}
      title="Delete keyword search"
    >
      <VoxelDivider />
      <VoxelDialogContent>
        <div className="flex flex-col gap-4 pt-4">
          <div>
            <VoxelText variant="form-caption">Name</VoxelText>
            <VoxelText>{requestAnalysis.request.name}</VoxelText>
          </div>
          <div>
            <VoxelText variant="form-caption">Type</VoxelText>
            <VoxelText>
              {
                SearchRequestTypes.find(
                  (st) => st.type === requestAnalysis.request.googleAdsRequestType
                )?.name
              }
            </VoxelText>
          </div>
          <div>
            <VoxelText variant="form-caption">Time</VoxelText>
            <VoxelText>
              {moment.utc(requestAnalysis.request.createdTime).local().format('yyyy-MM-DD hh:mm')}
            </VoxelText>
          </div>

          <div>
            <VoxelText variant="form-caption">Processing progress</VoxelText>
            <VoxelText>
              {requestAnalysis.completedCalls}/{requestAnalysis.totalCalls} completed
            </VoxelText>
            <VoxelText>
              {requestAnalysis.failedCalls}/{requestAnalysis.totalCalls} failed
            </VoxelText>
          </div>

          <div>
            <VoxelText variant="form-caption">Keywords</VoxelText>
            <VoxelText>{requestAnalysis.completedKeywords}</VoxelText>
          </div>
        </div>
      </VoxelDialogContent>
      <VoxelDivider />
      <VoxelDialogContent>
        <div className="pt-4">
          <VoxelText>This will also remove all of the associated keywords</VoxelText>
        </div>
      </VoxelDialogContent>
      <VoxelDialogActions>
        <VoxelDialogActionCancel onClick={deleting ? undefined : handleClose} />
        <VoxelDialogActionSubmit
          title="Delete"
          color="error"
          onClick={handleDelete}
          submitting={deleting}
        />
      </VoxelDialogActions>
    </VoxelDialog>
  );
};

export default DeleteRequestDialog;
